import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { of, race } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, map, take, tap, withLatestFrom } from 'rxjs/operators';
import { ChangeLogEntry, InfectionStatus } from '@mona/models';
import {
    applyInstancesChanges,
    ChangeLogAction,
    ChangeLogSelectors,
    getPersistedChangesData,
    getRevertedChangesForModel,
    getRevertedCreateIds,
    reconcileInstancesWithChanges,
} from '@mona/pdms/data-access-changelog';
import {
    applyLastChangedByToPrescriptions,
    withCurrentEncounterId,
    currentEncounterId,
    isEmptyEncounterId,
} from '@mona/pdms/data-access-combined';
import { PractitionersFacade } from '@mona/pdms/data-access-practitioners';
import { isSameEncounterFilter } from '@mona/shared/utils';
import { InfectionStatusApi } from '../../infrastructure';
import { InfectionStatusActions } from '../actions';
import { selectInfectionAll } from '../selectors';

/**
 * Infections effects
 */
@Injectable()
export class InfectionStatusEffects {
    changeLogMap$ = this.store.select(ChangeLogSelectors.getChangesMap);
    infectionStatusAll$ = this.store.select(selectInfectionAll);

    /** Load Infection Status */
    loadInfectionStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(InfectionStatusActions.loadInfectionStatus),
            withCurrentEncounterId(),
            concatMap(([, encounterId]) =>
                race(
                    isEmptyEncounterId(this.store).pipe(
                        take(1),
                        map(() => InfectionStatusActions.loadInfectionStatusCancelled({ reason: 'encounter-cleared' })),
                    ),
                    this.infectionStatusApi.getInfectionStatus(encounterId).pipe(
                        isSameEncounterFilter(currentEncounterId(this.store), encounterId),
                        withLatestFrom(this.changeLogMap$, this.practitionersFacade.practitionersMap$),
                        map(([data, changesMap, practitionersMap]) => {
                            const changes = changesMap['InfectionStatus'] || [];

                            if (changes.length) {
                                data = applyInstancesChanges(data, changes) as InfectionStatus[];
                            }

                            data = applyLastChangedByToPrescriptions(data, practitionersMap);

                            return InfectionStatusActions.loadInfectionStatusSuccess({ data });
                        }),
                        catchError(error => of(InfectionStatusActions.loadInfectionStatusFailure({ error }))),
                        defaultIfEmpty(
                            InfectionStatusActions.loadInfectionStatusCancelled({
                                reason: 'encounter-changed',
                            }) as Action,
                        ),
                    ),
                ),
            ),
        ),
    );

    /** Listen change save success & upsert entity + show message */
    onChangeSaved$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    ChangeLogAction.saveChangeAction.action,
                    ChangeLogAction.saveChangeAction.succeededAction,
                    ChangeLogAction.saveChangesAction.action,
                    ChangeLogAction.saveChangesAction.succeededAction,
                    ChangeLogAction.revertOptimisticChange,
                    ChangeLogAction.revertOptimisticChanges,
                    ChangeLogAction.revertStaleSavingEntries,
                ),
                withLatestFrom(this.infectionStatusAll$, this.changeLogMap$),
                tap(([action, data, changesMap]) => {
                    const changes: ChangeLogEntry<InfectionStatus>[] = changesMap['InfectionStatus'] || [];
                    const reverted = getRevertedChangesForModel(action, 'InfectionStatus');
                    const infectionStatuses = reconcileInstancesWithChanges(data, changes, reverted);

                    // A reverted optimistic create is filtered out of the reconciled array, but
                    // `upsertMany` never removes entities — so the phantom row would linger with
                    // its `isSaving` flag set. Explicitly remove reverted create ids (mirrors the
                    // entity-adapter effects, e.g. AdditionalDevices).
                    const revertedCreateIds = getRevertedCreateIds(reverted);
                    if (revertedCreateIds.length) {
                        this.store.dispatch(InfectionStatusActions.removeInfectionStatus({ ids: revertedCreateIds }));
                    }

                    if (infectionStatuses) {
                        this.store.dispatch(InfectionStatusActions.upsertInfectionStatus({ data: infectionStatuses }));
                    }
                }),
            ),
        { dispatch: false },
    );

    /** Listen change persist success & add one - realistic update */
    onChangePersisted$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ChangeLogAction.persistChangesAction.succeededAction),
                withLatestFrom(
                    this.infectionStatusAll$,
                    this.changeLogMap$,
                    this.practitionersFacade.practitionersMap$,
                ),
                tap(([, data, changesMap, practitionersMap]) => {
                    const changes = changesMap['InfectionStatus'] || [];

                    if (changes.length) {
                        const removeUpdateData = getPersistedChangesData(data, changes);
                        removeUpdateData.toUpdate = applyLastChangedByToPrescriptions(
                            removeUpdateData.toUpdate,
                            practitionersMap,
                        );

                        this.store.dispatch(
                            InfectionStatusActions.addInfectionStatusChanges({
                                toUpdateEntities: removeUpdateData.toUpdate || [],
                                toRemoveIds: removeUpdateData.toRemove.map(({ id }) => id) || [],
                            }),
                        );
                    }
                }),
            ),
        { dispatch: false },
    );

    /**
     * Constructor
     * @param store
     * @param actions$
     * @param infectionStatusApi
     * @param practitionersFacade
     */
    constructor(
        private store: Store<any>,
        private actions$: Actions,
        private infectionStatusApi: InfectionStatusApi,
        private practitionersFacade: PractitionersFacade,
    ) {}
}
